"use client";

import Image, { type StaticImageData } from "next/image";

import { Clock, RotateCcw } from "lucide-react";

import CompensationIcon from "../../assets/wallet/icon/compensation-booking.png";
import TicketPurchaseIcon from "../../assets/wallet/icon/ticket-purchase.png";
import TransferIcon from "../../assets/wallet/icon/transfer-to-account.png";
import { formatMovementDate, formatWalletAmount } from "../../utils/walletFormatters";

export interface WalletMovementItem {
  type: "refund" | "compensation" | "refund_in_progress" | "purchase" | "transfer";
  description: string;
  amount: number;
  date: string;
  flightInfo?: string;
  statusNote?: string;
  iconUrl?: string;
}

const iconByType: Record<
  Exclude<WalletMovementItem["type"], "refund" | "refund_in_progress">,
  { src: StaticImageData | string; alt: string }
> = {
  compensation: { src: CompensationIcon, alt: "Compensation" },
  purchase: { src: TicketPurchaseIcon, alt: "Purchase" },
  transfer: { src: TransferIcon, alt: "Transfer" },
};

const fmtAmount = (n: number) => formatWalletAmount(Math.abs(n));

export function MovementItem({ movement }: Readonly<{ movement: WalletMovementItem }>) {
  const isRefund = movement.type === "refund";
  const isInProgress = movement.type === "refund_in_progress";
  const icon =
    isRefund || isInProgress
      ? null
      : iconByType[
          movement.type as Exclude<WalletMovementItem["type"], "refund" | "refund_in_progress">
        ];

  let amountPrefix: string;
  if (isInProgress) {
    amountPrefix = "";
  } else if (movement.amount >= 0) {
    amountPrefix = "+ ";
  } else {
    amountPrefix = "- ";
  }

  return (
    <div
      className="flex items-center justify-between px-4 md:px-6 py-3 gap-3"
      data-testid="movement-item"
    >
      {/* Left: icon + text */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {/* Icon circle */}
        <div className="shrink-0 w-9 h-9 lg:w-[45px] lg:h-[45px] rounded-full bg-[#F7F7F7] flex items-center justify-center">
          {movement.iconUrl ? (
            <img
              src={movement.iconUrl}
              alt=""
              width={14}
              height={14}
              className="lg:w-4 lg:h-4"
              aria-hidden="true"
            />
          ) : (
            <>
              {isRefund && (
                <RotateCcw size={14} className="lg:w-4 lg:h-4 text-brand-cyan" aria-hidden="true" />
              )}
              {isInProgress && (
                <Clock size={14} className="lg:w-4 lg:h-4 text-brand-cyan" aria-hidden="true" />
              )}
              {icon && (
                <Image
                  src={icon.src}
                  alt={icon.alt}
                  width={14}
                  height={14}
                  className="lg:w-4 lg:h-4"
                />
              )}
            </>
          )}
        </div>

        {/* Text block */}
        <div className="flex flex-col gap-0.5 min-w-0">
          <p className="text-sm lg:text-base font-normal lg:leading-5 text-[#1B365D] truncate">
            {movement.description}
          </p>
          {movement.flightInfo && (
            <p className="text-xs lg:text-sm font-normal lg:leading-[18px] text-[#A1A1A1] truncate">
              {movement.flightInfo}
            </p>
          )}
          {movement.statusNote && (
            <p className="text-[11px] lg:text-xs font-medium lg:leading-[14px] text-[#FFA400]">
              {movement.statusNote}
            </p>
          )}
        </div>
      </div>

      {/* Right: amount + date */}
      <div className="shrink-0 flex flex-col items-end gap-1 lg:min-w-[130px]">
        {isInProgress ? (
          <div className="flex items-center gap-1.5">
            <Clock size={11} className="shrink-0 text-brand-cyan" aria-hidden="true" />
            <span
              className="text-sm lg:text-base font-medium lg:leading-5 text-[#1B365D]"
              data-testid="movement-amount"
            >
              ${fmtAmount(movement.amount)}
            </span>
          </div>
        ) : (
          <span
            className="text-sm lg:text-base font-medium lg:leading-5 text-[#00AEC7]"
            data-testid="movement-amount"
          >
            {amountPrefix}${fmtAmount(movement.amount)}
          </span>
        )}
        <p className="text-[11px] lg:text-sm font-medium lg:leading-[18px] text-[#A1A1A1]">
          {formatMovementDate(movement.date)}
        </p>
      </div>
    </div>
  );
}
